import React, { useEffect, useMemo, useState } from 'react';

import ChapterSelector from '../../Common/ChapterSelector/ChapterSelector';
import ModalBase from '../../ModalBase/ModalBase';

const MAX_QUESTIONS = 5000;

function PersonalizedChapterSelectorModal({
  isOpen,
  onClose,
  referenceData,
  isLoading,
  isSaving,
  saveSelectedChapters,
}) {
  const [selectedChapterIds, setSelectedChapterIds] = useState(new Set());

  // Pre-populate from referenceData.isSelected whenever data or open state changes
  useEffect(() => {
    if (!isOpen || !referenceData) return;

    const preSelected = new Set();
    (referenceData.references || []).forEach((ref) => {
      (ref.chapters || []).forEach((ch) => {
        if (ch.isSelected) preSelected.add(ch.id);
      });
    });
    setSelectedChapterIds(preSelected);
  }, [referenceData, isOpen]);

  // Map API shape to ChapterSelector shape
  // referenceData.references[].chapters[].questions is an array of { id }
  const references = useMemo(
    () =>
      (referenceData?.references || []).map((ref) => ({
        referenceId: ref.id,
        referenceName: ref.name,
        chapters: (ref.chapters || []).map((ch) => ({
          ...ch,
          questionCount: ch.totalQuestions ?? 0,
        })),
      })),
    [referenceData],
  );

  // Build a lookup: chapterId → questionCount
  const questionCountById = useMemo(() => {
    const map = {};
    references.forEach((ref) =>
      ref.chapters.forEach((ch) => {
        map[ch.id] = ch.questionCount;
      }),
    );
    return map;
  }, [references]);

  // Total questions across all selected chapters
  const totalQuestions = useMemo(
    () =>
      Array.from(selectedChapterIds).reduce(
        (sum, id) => sum + (questionCountById[id] || 0),
        0,
      ),
    [selectedChapterIds, questionCountById],
  );

  const overLimit = totalQuestions > MAX_QUESTIONS;
  const canSave = selectedChapterIds.size > 0 && !isSaving && !overLimit;

  const handleSelectionChange = (newSet) => {
    setSelectedChapterIds(newSet);
  };

  const handleSave = () => {
    if (!canSave) return;
    saveSelectedChapters(Array.from(selectedChapterIds));
  };

  return (
    <ModalBase
      isOpen={isOpen}
      toggle={!isSaving ? onClose : undefined}
      header="Personalized Study — Select Chapters"
      className="create-playlist-modal"
      body={
        <>
          <ChapterSelector
            references={references}
            selectedChapterIds={selectedChapterIds}
            onSelectionChange={handleSelectionChange}
            loading={isLoading}
          />

          {/* Question count indicator */}
          {selectedChapterIds.size > 0 && (
            <div
              className="mt-2 text-right"
              style={{
                fontSize: '0.85rem',
                color: overLimit ? '#dc3545' : '#6c757d',
                fontWeight: overLimit ? 'bold' : 'normal',
              }}
            >
              {totalQuestions.toLocaleString()} /{' '}
              {MAX_QUESTIONS.toLocaleString()} questions
              {overLimit && ' — reduce selection to continue'}
            </div>
          )}
        </>
      }
      footer={
        <>
          <button
            type="button"
            className="btn modal-base-btn secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            CANCEL
          </button>
          <button
            type="button"
            className="btn modal-base-btn primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            {isSaving ? 'SAVING...' : 'SAVE'}
          </button>
        </>
      }
    />
  );
}

export default PersonalizedChapterSelectorModal;
